import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Pre-fills the Tawk.to visitor identity (name + email) for logged-in users
 * so support agents see the trader's real name instead of "Visitor #1234".
 *
 * - Runs on every auth state change.
 * - Tawk loads asynchronously, so we wait via Tawk_API.onLoad if needed.
 * - On logout, we clear name/email so subsequent anonymous chats don't leak
 *   the previous trader's info.
 */
export default function TawkVisitor() {
  const { user } = useAuth();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const apply = () => {
      const api = window.Tawk_API;
      if (!api) return;

      if (user?.email) {
        const name = user.name || user.email.split('@')[0];
        // visitor must be assigned BEFORE setAttributes for first-time chats
        api.visitor = { name, email: user.email };
        if (typeof api.setAttributes === 'function') {
          api.setAttributes(
            {
              name,
              email: user.email,
              role: user.role || 'trader',
              referral_code: user.referral_code || '',
              user_id: user.user_id || '',
            },
            () => {}
          );
        }
      } else {
        // Logged out — wipe identity so the chat is anonymous again.
        api.visitor = { name: '', email: '' };
        if (typeof api.setAttributes === 'function') {
          api.setAttributes({ name: '', email: '', role: '', user_id: '' }, () => {});
        }
      }
    };

    if (window.Tawk_API && (window.Tawk_API.onLoaded || typeof window.Tawk_API.setAttributes === 'function')) {
      apply();
    } else {
      // Tawk hasn't finished loading yet — register an onLoad hook.
      window.Tawk_API = window.Tawk_API || {};
      const prev = window.Tawk_API.onLoad;
      window.Tawk_API.onLoad = function () {
        if (typeof prev === 'function') prev();
        apply();
      };
    }
  }, [user]);

  return null;
}
